import React, { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_BASE_URL } from '../../config';
import Modal from '../../components/Modal';
import SimpleForm from '../../components/SimpleForm';
import { Video, Plus, ChevronLeft, ChevronRight, Trash2 } from 'lucide-react';

const MONTH_NAMES = [
    'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
    'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];
const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

function buildMonthCells(year, month) {
    const first = new Date(year, month - 1, 1);
    let mondayOffset = first.getDay() - 1;
    if (mondayOffset < 0) mondayOffset = 6;
    const daysInMonth = new Date(year, month, 0).getDate();
    const cells = [];
    for (let i = 0; i < mondayOffset; i += 1) cells.push(null);
    for (let d = 1; d <= daysInMonth; d += 1) cells.push(d);
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
}

function dateKey(year, month, day) {
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

const occupancyTitle = (busy, { withChannel = false } = {}) => {
    if (!busy?.length) return 'Свободно';
    return busy
        .map((b) => {
            const who = b.group_name || 'занято';
            return withChannel && b.channel_name ? `${b.channel_name}: ${who}` : who;
        })
        .join('\n');
};

const ChannelCalendar = ({ year, month, occupancy, withChannel = false, multiHighlight = false }) => {
    const cells = useMemo(() => buildMonthCells(year, month), [year, month]);

    return (
        <div className="zoom-channel-calendar">
            <div className="zoom-cal-weekdays">
                {WEEKDAYS.map((w) => (
                    <span key={w} className="zoom-cal-weekday">{w}</span>
                ))}
            </div>
            <div className="zoom-cal-grid">
                {cells.map((day, idx) => {
                    if (!day) {
                        return <div key={`e-${idx}`} className="zoom-cal-cell zoom-cal-empty" />;
                    }
                    const key = dateKey(year, month, day);
                    const busy = occupancy?.[key] || [];
                    const channelCount = multiHighlight
                        ? new Set(busy.map((b) => b.channel_id || b.channel_name)).size
                        : 0;
                    const multi = multiHighlight && channelCount > 1;
                    return (
                        <div
                            key={key}
                            className={`zoom-cal-cell ${busy.length ? 'zoom-cal-busy' : ''} ${multi ? 'zoom-cal-multi' : ''}`}
                            title={occupancyTitle(busy, { withChannel })}
                        >
                            <span className="zoom-cal-day">{day}</span>
                            {busy.length > 0 && (
                                <span className="zoom-cal-count" aria-hidden>
                                    {multiHighlight ? channelCount : '•'}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const channelFields = [
    { name: 'name', label: 'Название канала', required: true, placeholder: 'Канал 1' },
    { name: 'zoom_url', label: 'Ссылка Zoom', required: true, placeholder: 'https://zoom.us/j/...' },
    { name: 'organizer_code', label: 'Код организатора', placeholder: 'например 123456' },
    {
        name: 'description',
        label: 'Описание',
        type: 'textarea',
        rows: 8,
        placeholder: 'Тема: Канал\nВремя: ...\nПодключиться к конференции Zoom\n...'
    }
];

const ZoomChannelsTab = () => {
    const queryClient = useQueryClient();
    const now = new Date();
    const [year, setYear] = useState(now.getFullYear());
    const [month, setMonth] = useState(now.getMonth() + 1);
    const [createOpen, setCreateOpen] = useState(false);
    const [editChannel, setEditChannel] = useState(null);
    const [saving, setSaving] = useState(false);
    const [distributing, setDistributing] = useState(false);

    const { data, isLoading, refetch } = useQuery(
        ['zoomChannels', year, month],
        async () => {
            const res = await axios.get(
                `${API_BASE_URL}/admin/zoom/channels?year=${year}&month=${month}`,
                { withCredentials: true }
            );
            return res.data;
        }
    );

    const channels = data?.items || [];

    const shiftMonth = (delta) => {
        let m = month + delta;
        let y = year;
        if (m < 1) {
            m = 12;
            y -= 1;
        } else if (m > 12) {
            m = 1;
            y += 1;
        }
        setMonth(m);
        setYear(y);
    };

    const handleCreate = async (formData) => {
        setSaving(true);
        try {
            await axios.post(
                `${API_BASE_URL}/admin/zoom/channels`,
                {
                    name: formData.name,
                    zoom_url: formData.zoom_url,
                    organizer_code: formData.organizer_code,
                    description: formData.description
                },
                { withCredentials: true }
            );
            toast.success('Канал создан');
            setCreateOpen(false);
            refetch();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Не удалось создать канал');
        } finally {
            setSaving(false);
        }
    };

    const handleUpdate = async (formData) => {
        if (!editChannel) return;
        setSaving(true);
        try {
            await axios.put(
                `${API_BASE_URL}/admin/zoom/channels/${editChannel.id}`,
                {
                    name: formData.name,
                    zoom_url: formData.zoom_url,
                    organizer_code: formData.organizer_code,
                    description: formData.description,
                    is_active: formData.is_active === 'true' || formData.is_active === true
                },
                { withCredentials: true }
            );
            toast.success('Канал обновлён');
            setEditChannel(null);
            refetch();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Не удалось обновить канал');
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (channel) => {
        if (!window.confirm(`Удалить канал «${channel.name}»?`)) return;
        try {
            await axios.delete(`${API_BASE_URL}/admin/zoom/channels/${channel.id}`, {
                withCredentials: true
            });
            toast.success('Канал удалён');
            refetch();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Не удалось удалить канал');
        }
    };

    const handleDistribute = async () => {
        setDistributing(true);
        try {
            const res = await axios.post(
                `${API_BASE_URL}/admin/zoom/distribute`,
                {},
                { withCredentials: true }
            );
            const assigned = res.data?.assigned ?? 0;
            const total = res.data?.total ?? 0;
            toast.success(`Распределено: ${assigned} из ${total} будущих вебинаров`);
            queryClient.invalidateQueries(['zoomChannels']);
            refetch();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Ошибка распределения');
        } finally {
            setDistributing(false);
        }
    };

    const formatDateRu = (iso) => {
        if (!iso) return '—';
        const [y, m, d] = String(iso).split('-');
        return `${d}.${m}.${y}`;
    };

    const handleDeleteEvent = async (event) => {
        const isBooking = event.kind === 'booking';
        const label = isBooking
            ? `личную выдачу «${event.label}»`
            : `назначение канала у вебинара «${event.label}»`;
        if (!window.confirm(`Удалить ${label}?`)) return;
        try {
            if (isBooking) {
                await axios.delete(`${API_BASE_URL}/admin/zoom/bookings/${event.id}`, {
                    withCredentials: true
                });
            } else {
                await axios.delete(`${API_BASE_URL}/admin/zoom/groups/${event.id}/assignment`, {
                    withCredentials: true
                });
            }
            toast.success('Удалено');
            refetch();
        } catch (e) {
            toast.error(e.response?.data?.message || 'Не удалось удалить');
        }
    };

    if (isLoading) {
        return <div className="loading"><div className="spinner" /></div>;
    }

    return (
        <div className="zoom-channels-tab">
            <div className="zoom-channels-header">
                <div>
                    <h2><Video size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />Zoom</h2>
                    <p className="zoom-channels-hint">
                        Каналы со ссылками Zoom. Занятые даты — вебинары и личные выдачи.
                    </p>
                </div>
                <div className="zoom-channels-actions">
                    <button type="button" className="btn btn-secondary" onClick={() => setCreateOpen(true)}>
                        <Plus size={16} /> Канал
                    </button>
                    <button
                        type="button"
                        className="btn btn-primary"
                        onClick={handleDistribute}
                        disabled={distributing || channels.length === 0}
                    >
                        {distributing ? 'Распределение…' : 'Распределить каналы'}
                    </button>
                </div>
            </div>

            <div className="zoom-month-nav">
                <button type="button" className="btn btn-secondary" onClick={() => shiftMonth(-1)} aria-label="Предыдущий месяц">
                    <ChevronLeft size={18} />
                </button>
                <strong>{MONTH_NAMES[month - 1]} {year}</strong>
                <button type="button" className="btn btn-secondary" onClick={() => shiftMonth(1)} aria-label="Следующий месяц">
                    <ChevronRight size={18} />
                </button>
            </div>

            {channels.length === 0 ? (
                <div className="zoom-empty">Пока нет каналов. Создайте первый и нажмите «Распределить каналы».</div>
            ) : (
                <div className="zoom-channels-columns">
                    {channels.map((ch) => (
                        <div key={ch.id} className={`zoom-channel-column ${ch.is_active ? '' : 'zoom-channel-inactive'}`}>
                            <div className="zoom-channel-head">
                                <h3>{ch.name}</h3>
                                {!ch.is_active && <span className="zoom-inactive-badge">выкл.</span>}
                            </div>
                            <a
                                className="zoom-channel-url"
                                href={ch.zoom_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={ch.zoom_url}
                            >
                                {ch.zoom_url}
                            </a>
                            {ch.organizer_code && (
                                <div className="zoom-channel-meta">
                                    Код организатора: <strong>{ch.organizer_code}</strong>
                                </div>
                            )}
                            {ch.description && (
                                <pre className="zoom-channel-description">{ch.description}</pre>
                            )}
                            <div className="zoom-channel-meta">
                                Назначено групп: {ch.assigned_groups_count || 0}
                            </div>
                            <ChannelCalendar year={year} month={month} occupancy={ch.occupancy} />
                            {(ch.events || []).length > 0 && (
                                <ul className="zoom-channel-events">
                                    {ch.events.map((ev) => (
                                        <li key={`${ev.kind}-${ev.id}`} className="zoom-channel-event">
                                            <div className="zoom-channel-event-info">
                                                <span className="zoom-channel-event-kind">
                                                    {ev.kind === 'booking' ? 'Личная' : 'Вебинар'}
                                                </span>
                                                <strong>{ev.label}</strong>
                                                <span className="zoom-channel-event-dates">
                                                    {ev.date_from === ev.date_to
                                                        ? formatDateRu(ev.date_from)
                                                        : `${formatDateRu(ev.date_from)} – ${formatDateRu(ev.date_to)}`}
                                                    {ev.slots?.length
                                                        ? ` · ${ev.slots
                                                            .map((s) => [s.time_from, s.time_to].filter(Boolean).join('–'))
                                                            .filter(Boolean)
                                                            .join(', ')}`
                                                        : ''}
                                                </span>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-secondary zoom-event-delete"
                                                title="Удалить с канала"
                                                onClick={() => handleDeleteEvent(ev)}
                                            >
                                                <Trash2 size={14} />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="zoom-channel-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setEditChannel(ch)}>
                                    Изменить
                                </button>
                                <button type="button" className="btn btn-secondary" onClick={() => handleDelete(ch)}>
                                    Удалить
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {createOpen && (
                <Modal isOpen title="Новый канал Zoom" onClose={() => setCreateOpen(false)} size="large">
                    <SimpleForm
                        fields={channelFields}
                        onSubmit={handleCreate}
                        onCancel={() => setCreateOpen(false)}
                        loading={saving}
                    />
                </Modal>
            )}

            {editChannel && (
                <Modal isOpen title="Редактировать канал" onClose={() => setEditChannel(null)} size="large">
                    <SimpleForm
                        initialData={{
                            name: editChannel.name,
                            zoom_url: editChannel.zoom_url,
                            organizer_code: editChannel.organizer_code || '',
                            description: editChannel.description || '',
                            is_active: editChannel.is_active ? 'true' : 'false'
                        }}
                        fields={[
                            ...channelFields,
                            {
                                name: 'is_active',
                                label: 'Активен',
                                type: 'select',
                                options: [
                                    { value: 'true', label: 'Да' },
                                    { value: 'false', label: 'Нет' }
                                ]
                            }
                        ]}
                        onSubmit={handleUpdate}
                        onCancel={() => setEditChannel(null)}
                        loading={saving}
                    />
                </Modal>
            )}
        </div>
    );
};

export default ZoomChannelsTab;
